import { type FC, useState, type MouseEvent } from "react";

import DragHandleIcon from "@mui/icons-material/DragHandle";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import IconButton from "@mui/material/IconButton";
import { colorsSelector } from "@redux/colorPalette/selectors";
import {
	pushTopToWaitingList,
	saveCurrentSong,
	useRemoveFromWaitingList,
	useSetLastSong,
} from "@redux/player";
import analytics from "lib/analytics";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import type { Song } from "src/@types/api-types";

import PopoverMenu from "components/Lego/PopoverMenu/PopoverMenu";
import FlashMessage from "components/features/FlashMessage/FlashMessage";

import {
	StyledListItemText,
	StyledListItemSecondaryAction,
	StyledElapsedTimeIcon,
	DragHandleIconContainer,
} from "./SongListItem.style";
import type { DraggableAttributes } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { bamSessionUserSelector, favoritePlaylistSelector } from "@redux/session/selectors";
import { userContextSelector } from "@redux/user/selectors";
import { addToPlaylist, getCustomerPlaylists, removeFromPlaylist } from "services/networking/playlists";
import { setCustomPlayLists } from "@redux/session";
import { UserContext } from "shared-types/user";

interface ISongListItem {
	song: Song;
	index: number;
	isLiked: boolean;
	elapsedTime?: boolean;
	dragHandleListeners?: SyntheticListenerMap | undefined;
	dragHandleAttributes?: DraggableAttributes;
}

const SongListItem: FC<ISongListItem> = ({
	song,
	index,
	elapsedTime,
	isLiked,
	dragHandleListeners,
	dragHandleAttributes,
}) => {
	const dispatch = useDispatch();
	const intl = useIntl();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [showFlashMessage, setShowFlashMessage] = useState(false);
	const favoritePlaylist = useSelector(favoritePlaylistSelector);
	const [showFlashMessageMaxSongs, setShowFlashMessageMaxSongs] = useState(false);
	const customer = useSelector(bamSessionUserSelector)
	const userContext = useSelector(userContextSelector)
	const customerId = customer?.clientCode;

	const colors = useSelector(colorsSelector);

	const { setLastSong } = useSetLastSong();

	const { removeFromWaitingList } = useRemoveFromWaitingList();

	const waitingListSong = {
		index: index,
		song: song,
	};

	const songTrackedData = {
		songID: song.bamId,
		songTitle: song.title,
		songArtist: song.artist,
	};

	const openOptions = (e: MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget);
	};

	const closeMenu = () => {
		setAnchorEl(null);
	};

	const closeFlashMessage = () => setShowFlashMessage(false);

	const play = () => {
		removeFromWaitingList(index);
		dispatch(saveCurrentSong(song));
		closeMenu();
		analytics.track("waitingList_popover_play", songTrackedData);
	};
	const playNext = () => {
		removeFromWaitingList(index);
		dispatch(pushTopToWaitingList(song));
		closeMenu();
		analytics.track("waitingList_popover_playNext", songTrackedData);
	};
	const setAsLastSong = () => {
		setLastSong(waitingListSong.song);
		closeMenu();
		analytics.track("waitingList_popover_playLast", songTrackedData);
	};

	const removeSongFromWaitingList = () => {
		removeFromWaitingList(index);
		setShowFlashMessage(true);
		closeMenu();
		analytics.track("waitingList_popover_delete", songTrackedData);
	};

	const handleFavoriteClick = async () => {
		if (!favoritePlaylist) return
		if (isLiked) {
			await removeFromPlaylist(song.id, favoritePlaylist.id);
			await refrechPlaylists()
		}
		else {
			if ((favoritePlaylist?.playlistSongs ?? []).length >= 100) {
				setShowFlashMessageMaxSongs(true);
				return;
			}
			await addToPlaylist(song.id, favoritePlaylist.id);
			await refrechPlaylists()
		}
		closeMenu();
	};

	const refrechPlaylists = async () => {
		if (!customerId) return;
		const playlists = await getCustomerPlaylists(customerId);
		dispatch(setCustomPlayLists(playlists));
	};

	return (
		<div style={{ width: "100%" }}>
			<StyledListItemText
				primary={song.title}
				secondary={song.artist?.name}
				colors={colors}
				elapsedtime={elapsedTime}
				onClick={openOptions}
			/>
			<StyledListItemSecondaryAction
				color="inherit"
			>
				<IconButton color="inherit" aria-label="more" onClick={openOptions}>
					{elapsedTime ? (
						<StyledElapsedTimeIcon colors={colors} />
					) : (
						<MoreHorizIcon />
					)}
				</IconButton>
				<DragHandleIconContainer
					{...dragHandleListeners}
					{...dragHandleAttributes}
				>
					<DragHandleIcon />
				</DragHandleIconContainer>

				<PopoverMenu
					removeFromWaitingList={removeSongFromWaitingList}
					{...{
						play,
						playNext,
						setAsLastSong,
						anchorEl,
						closeMenu,
					}}
					handleFavoriteClick={(favoritePlaylist && userContext === UserContext.BAM) ? handleFavoriteClick : undefined}
					isFavorite={isLiked}
				/>
			</StyledListItemSecondaryAction>
			<FlashMessage
				message={intl.formatMessage({
					id: "flash-messages.waiting-list-delete",
				})}
				isShown={showFlashMessage}
				onClose={closeFlashMessage}
			/>
			<FlashMessage
				isShown={showFlashMessageMaxSongs}
				message={intl.formatMessage({
					id: "playlist.maxSongs"
				})}
				onClose={() => {
					setShowFlashMessageMaxSongs(false);
				}}
			/>
		</div>
	);
};

export default SongListItem;
